/**
 * RENDER module — noir post-processing pipeline.
 *
 * Chain: RenderPass (HDR) → UnrealBloomPass (thresholded, pre-tonemap, so
 * only emissive/neon HDR values bloom) → OutputPass (tone mapping + sRGB)
 * → noir grade ShaderPass (B&W, crush, sepia, grain, vignette) in display
 * space. Scene fog (FogExp2) is owned and updated here as well.
 */
import { Color, FogExp2, HalfFloatType, ShaderMaterial, Vector2, WebGLRenderTarget } from 'three'
import type { PerspectiveCamera, Scene, WebGLRenderer } from 'three'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import type { NoirPostConfig } from './config'
import { MSAA_SAMPLES } from './constants'
import {
  createNoirShaderUniforms,
  NOIR_FRAGMENT_SHADER,
  NOIR_VERTEX_SHADER
} from './noir-shader'
import type { NoirShaderUniforms } from './noir-shader'

export interface NoirPipeline {
  /** Render one frame; elapsedSeconds drives the animated grain seed. */
  render(elapsedSeconds: number): void
  /** Resize the composer and all passes (CSS pixels). */
  setSize(width: number, height: number): void
  /** Apply a partial config update at runtime. */
  setConfig(partial: Partial<NoirPostConfig>): void
  /** Current effective config (immutable snapshot). */
  getConfig(): NoirPostConfig
}

const effectivePixelRatio = (config: NoirPostConfig): number =>
  Math.min(window.devicePixelRatio, config.maxPixelRatio)

export const createNoirPipeline = (
  renderer: WebGLRenderer,
  scene: Scene,
  camera: PerspectiveCamera,
  initialConfig: NoirPostConfig
): NoirPipeline => {
  let config = initialConfig
  let width = window.innerWidth
  let height = window.innerHeight

  const fog = new FogExp2(config.fogColor, config.fogDensity)
  scene.fog = fog
  // Sky matches the fog so rooflines read as silhouettes against haze
  // instead of merging into a void.
  const background = new Color(config.fogColor)
  scene.background = background

  const hdrTarget = new WebGLRenderTarget(width, height, {
    type: HalfFloatType,
    samples: MSAA_SAMPLES
  })
  const composer = new EffectComposer(renderer, hdrTarget)

  const renderPass = new RenderPass(scene, camera)
  const bloomPass = new UnrealBloomPass(
    new Vector2(width, height),
    config.bloomStrength,
    config.bloomRadius,
    config.bloomThreshold
  )
  const outputPass = new OutputPass()
  // Pass a ShaderMaterial (not a plain shader object) so ShaderPass uses
  // these uniform instances directly instead of cloning them.
  const noirUniforms: NoirShaderUniforms = createNoirShaderUniforms()
  const noirMaterial = new ShaderMaterial({
    uniforms: noirUniforms,
    vertexShader: NOIR_VERTEX_SHADER,
    fragmentShader: NOIR_FRAGMENT_SHADER
  })
  const noirPass = new ShaderPass(noirMaterial)

  composer.addPass(renderPass)
  composer.addPass(bloomPass)
  composer.addPass(outputPass)
  composer.addPass(noirPass)

  const applyConfig = (): void => {
    const pixelRatio = effectivePixelRatio(config)
    renderer.setPixelRatio(pixelRatio)
    renderer.toneMappingExposure = config.toneMappingExposure
    composer.setPixelRatio(pixelRatio)
    composer.setSize(width, height)

    fog.color.setHex(config.fogColor)
    fog.density = config.fogDensity
    background.setHex(config.fogColor)

    bloomPass.strength = config.bloomStrength
    bloomPass.radius = config.bloomRadius
    bloomPass.threshold = config.bloomThreshold
    bloomPass.enabled = config.bloomStrength > 0

    noirUniforms.uBwMix.value = config.bwMix
    noirUniforms.uBlackPoint.value = config.blackPoint
    noirUniforms.uWhitePoint.value = config.whitePoint
    noirUniforms.uSepiaAmount.value = config.sepiaAmount
    noirUniforms.uGrainIntensity.value = config.grainIntensity
    noirUniforms.uGrainSizePx.value = config.grainSizePx
    noirUniforms.uVignetteStrength.value = config.vignetteStrength
    noirUniforms.uResolution.value.set(width * pixelRatio, height * pixelRatio)
  }

  applyConfig()

  return {
    render(elapsedSeconds: number): void {
      noirUniforms.uTime.value = elapsedSeconds
      composer.render()
    },
    setSize(nextWidth: number, nextHeight: number): void {
      width = nextWidth
      height = nextHeight
      renderer.setSize(width, height)
      bloomPass.setSize(width, height)
      applyConfig()
    },
    setConfig(partial: Partial<NoirPostConfig>): void {
      config = { ...config, ...partial }
      applyConfig()
    },
    getConfig(): NoirPostConfig {
      return { ...config }
    }
  }
}
